import {Injectable} from "@angular/core";
import {MatDialog, MatDialogRef} from "@angular/material/dialog";
import {BehaviorSubject, Observable, Subscription, timer} from "rxjs";
import {tap} from "rxjs/operators";
import {PushJobsControllerService} from "../api/controllers/push-jobs/push-jobs-controller.service";
import {
  IPushJobStatus,
  PushJobKind,
  PushJobRequest,
  TERMINAL_PUSH_JOB_STATUSES,
} from "../api/controllers/push-jobs/dto/push-job";
import {
  IPushProgressDialogData,
  PushProgressDialogComponent,
} from "../../features/push/push-progress-dialog/push-progress-dialog.component";
import {AuthService} from "./auth.service";

/** Everything the service tracks for one kind. Kinds are independent: an exercise push and a
 * workout push can be in flight at the same time, each with its own poll timer and dialog. */
interface KindState {
  subject: BehaviorSubject<IPushJobStatus | null>;
  pollTimer?: Subscription;
  dialogRef?: MatDialogRef<PushProgressDialogComponent>;
  restored: boolean;
  restoring: boolean;
  consecutiveErrors: number;
}

@Injectable({providedIn: "root"})
export class PushProgressService {
  private readonly states = new Map<PushJobKind, KindState>();

  constructor(
    private pushJobs: PushJobsControllerService,
    private dialog: MatDialog,
    private authService: AuthService,
  ) {}

  private state(kind: PushJobKind): KindState {
    let state = this.states.get(kind);
    if (!state) {
      state = {
        subject: new BehaviorSubject<IPushJobStatus | null>(null),
        restored: false,
        restoring: false,
        consecutiveErrors: 0,
      };
      this.states.set(kind, state);
    }
    return state;
  }

  job$(kind: PushJobKind): Observable<IPushJobStatus | null> {
    return this.state(kind).subject.asObservable();
  }

  currentJob(kind: PushJobKind): IPushJobStatus | null {
    return this.state(kind).subject.value;
  }

  create(kind: PushJobKind, request: PushJobRequest): Observable<IPushJobStatus> {
    return this.pushJobs.create(kind, request).pipe(
      tap((job) => this.track(kind, job, true)),
    );
  }

  restore(kind: PushJobKind, openDialog = false): void {
    if (!this.authService.checkTokenAccess("SuperAdmin")) {
      this.logout();
      return;
    }

    const state = this.state(kind);
    if (state.restoring || state.restored) return;
    state.restoring = true;
    this.pushJobs.getCurrent(kind).subscribe({
      next: (job) => {
        state.restored = true;
        state.restoring = false;
        if (!job) {
          this.stopPolling(kind);
          state.subject.next(null);
          return;
        }
        this.track(kind, job, openDialog);
      },
      error: () => {
        state.restoring = false;
      },
    });
  }

  openActiveJobIfOwned(kind: PushJobKind): void {
    if (!this.authService.checkTokenAccess("SuperAdmin")) return;
    this.pushJobs.getCurrent(kind).subscribe({
      next: (job) => {
        if (job && !this.isTerminal(job)) this.track(kind, job, true);
      },
    });
  }

  track(kind: PushJobKind, job: IPushJobStatus, openDialog = false): void {
    const state = this.state(kind);
    state.restored = true;
    state.subject.next(job);
    if (this.isTerminal(job)) {
      this.stopPolling(kind);
    } else {
      state.consecutiveErrors = 0;
      this.schedulePoll(kind, 2000);
    }
    if (openDialog) this.openDialog(kind);
  }

  openDialog(kind: PushJobKind): void {
    const state = this.state(kind);
    if (!state.subject.value) return;
    if (state.dialogRef) {
      state.dialogRef.addPanelClass("push-progress-dialog--focused");
      return;
    }

    const data: IPushProgressDialogData = {
      kind,
      job$: this.job$(kind),
      cancel: () => this.cancel(kind),
      retry: () => this.retry(kind),
      downloadLog: () => this.downloadLog(kind),
      acknowledge: () => this.acknowledge(kind),
    };
    state.dialogRef = this.dialog.open(PushProgressDialogComponent, {
      width: "680px",
      maxWidth: "95vw",
      maxHeight: "90vh",
      disableClose: true,
      data,
    });
    state.dialogRef.afterClosed().subscribe(() => {
      state.dialogRef = undefined;
    });
  }

  cancel(kind: PushJobKind): Observable<IPushJobStatus> {
    const job = this.requireJob(kind);
    return this.pushJobs.cancel(kind, job.id).pipe(
      tap((updated) => this.track(kind, updated)),
    );
  }

  retry(kind: PushJobKind): Observable<IPushJobStatus> {
    const job = this.requireJob(kind);
    return this.pushJobs.retry(kind, job.id).pipe(
      tap((retryJob) => this.track(kind, retryJob)),
    );
  }

  downloadLog(kind: PushJobKind): Observable<Blob> {
    const job = this.requireJob(kind);
    return this.pushJobs.downloadLog(kind, job.id);
  }

  acknowledge(kind: PushJobKind): Observable<void> {
    const job = this.requireJob(kind);
    return this.pushJobs.acknowledge(kind, job.id).pipe(
      tap(() => {
        this.stopPolling(kind);
        this.state(kind).subject.next(null);
      }),
    );
  }

  logout(): void {
    this.states.forEach((state, kind) => {
      this.stopPolling(kind);
      state.dialogRef?.close();
      state.dialogRef = undefined;
      state.subject.next(null);
      state.restored = false;
      state.restoring = false;
      state.consecutiveErrors = 0;
    });
  }

  isTerminal(job: IPushJobStatus): boolean {
    return TERMINAL_PUSH_JOB_STATUSES.includes(job.status);
  }

  private schedulePoll(kind: PushJobKind, delayMs: number): void {
    this.stopPolling(kind);
    const state = this.state(kind);
    const jobId = state.subject.value?.id;
    if (!jobId) return;

    state.pollTimer = timer(delayMs).subscribe(() => {
      this.pushJobs.get(kind, jobId).subscribe({
        next: (job) => {
          if (state.subject.value?.id !== job.id) return;
          state.subject.next(job);
          state.consecutiveErrors = 0;
          if (this.isTerminal(job)) {
            this.stopPolling(kind);
          } else {
            this.schedulePoll(kind, 2000);
          }
        },
        error: () => {
          if (state.subject.value?.id !== jobId) return;
          state.consecutiveErrors += 1;
          const backoff = Math.min(
            30000,
            2000 * Math.pow(2, Math.min(state.consecutiveErrors, 4)),
          );
          this.schedulePoll(kind, backoff);
        },
      });
    });
  }

  private stopPolling(kind: PushJobKind): void {
    const state = this.state(kind);
    state.pollTimer?.unsubscribe();
    state.pollTimer = undefined;
  }

  private requireJob(kind: PushJobKind): IPushJobStatus {
    const job = this.state(kind).subject.value;
    if (!job) throw new Error(`There is no ${kind.toLowerCase()} push job to update.`);
    return job;
  }
}
